import { toast } from "react-toastify";

// Prefix of useAchievements' query key. Under ["dashboard"], so status and
// entry writes already refresh it with the other dashboard rows.
export const ACHIEVEMENTS_KEY = ["dashboard", "achievements"];

/**
 * Query filter for a user's library lists (["infStatuses" | "infJournal",
 * username, contentType, status, {...}]) that also matches the All lists — an
 * item lives in both its own type's list and All's, so every update here has
 * to reach both. `status` (optional) narrows to one status's lists.
 */
const libraryLists = (name, username, contentType, status) => ({
  predicate: ({ queryKey }) =>
    queryKey[0] === name &&
    queryKey[1] === username &&
    (queryKey[2] === contentType || queryKey[2] === "all") &&
    (status === undefined || queryKey[3] === status),
});

const invalidateTVJournalSummary = ({ queryClient, contentType }) => {
  if (contentType !== "tvshow") return;

  // Invalidate every cached summary by key prefix rather than resolving this
  // content's series id first. The season → series map that resolution relied
  // on is written with setQueryData and has no observers, so React Query
  // garbage-collects it after gcTime — leaving the summary stale exactly when
  // the modal had been open long enough to matter. Only one summary is ever on
  // screen (inside the open modal), and invalidateQueries refetches active
  // queries only, so the wider net costs at most the one refetch we want.
  queryClient.invalidateQueries({
    queryKey: ["TVJournalSummary"],
  });
};

/**
 * Applies the `affectedContentIds` the backend reports on every status
 * mutation — other content whose status row was created, changed, or deleted
 * as a side effect of the write (a season write recomputing its series, a
 * series "finished" cascading down to every season, a deleted journal entry
 * dropping or sweeping season rows). The client can't derive these new values
 * itself, so this is the only signal that they moved:
 *
 * - refetches the cached status ([\"contentStatus\", id]) — staleTime:
 *   Infinity means nothing would otherwise refresh on its own.
 * - drops the id from every cached infStatuses page. Side-effect rows are
 *   always SYSTEM-sourced (a direct user write reclaims USER ownership, see
 *   create_status/edit_status), and get_all_statuses excludes SYSTEM rows —
 *   so an affected id can never belong in a library list, and this never
 *   needs to refetch those pages to know that.
 */
export const applyAffectedStatusesToCache = ({
  queryClient,
  contentIds,
  contentType,
  username,
}) => {
  (contentIds ?? []).forEach((contentId) => {
    queryClient.invalidateQueries({
      queryKey: ["contentStatus", contentId],
    });

    queryClient.setQueriesData(
      libraryLists("infStatuses", username, contentType),
      (oldData) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            items: page.items.filter((item) => item.content_id !== contentId),
          })),
        };
      },
    );
  });
};

export const updateContentStatusInCache = ({
  queryClient,
  contentId,
  contentType,
  oldStatus,
  newStatus, // new status received from backend
  entryObject, // new entry object associated with change of status received from backend
  username,
}) => {
  // directly overwrite the status cache with new data
  queryClient.setQueryData(["contentStatus", contentId], () => {
    return newStatus;
  });

  // if new entry created as part of status change, add the new entry to the top of the journal cache
  // this implicitly assumes that the most recent journals are always shown at the top (true at time of writing)
  if (entryObject) {
    if (queryClient.getQueryData(["journal", contentId])) {
      queryClient.setQueryData(["journal", contentId], (oldData) => [
        entryObject,
        ...oldData,
      ]);
    }
  }

  // remove the old status from infStatuses cache
  queryClient.setQueriesData(
    {
      predicate: (query) =>
        libraryLists("infStatuses", username, contentType).predicate(query) &&
        (query.queryKey[3] === oldStatus || // targets only the infStatuses queries keyed by oldStatus if it exists
          (!oldStatus && query.queryKey[3] !== newStatus)), // if oldStatus doesn't exist, cast a wider net and remove
      // it from all queries keyed by a status that is not equal to newStatus
    },
    (oldData) => {
      if (!oldData) return oldData;

      return {
        ...oldData,
        pages: oldData.pages.map((page) => ({
          ...page,
          items: page.items.filter((item) => item.content_id !== contentId),
        })),
      };
    },
  );

  // invalidate the infStatuses query corresponding to new status so it can be inserted in correct location
  // (cannot be clever here as filters mean we can't just insert the new status at the top or bottom)
  queryClient.invalidateQueries(
    libraryLists("infStatuses", username, contentType, newStatus),
  );

  // if new status is finished, invalidate infJournal also because this queries journal entries not statuses
  // and we need to ensure the new entry is inserted at the correct position (again non trivial due to filters)
  newStatus === "finished" &&
    queryClient.invalidateQueries(
      libraryLists("infJournal", username, contentType),
    );

  // for tvshows, need to invalidate the TVJournalSummary if any season statuses mutate
  invalidateTVJournalSummary({
    queryClient,
    contentType,
  });

  // Explore-page collection cards display a viewer-specific "finished %" computed server-side.
  // Mark the queries stale (refetchType "none" avoids refetching while the explore page is in view);
  // the next mount of CollectionSection picks up the invalidation via refetchOnMount.
  queryClient.invalidateQueries({
    queryKey: ["exploreCollections"],
    refetchType: "none",
  });

  // The profile dashboard's rows (e.g. "In progress") are derived from statuses
  queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  // Track's summary strip is counted from the same rows
  queryClient.invalidateQueries({ queryKey: ["librarySummary"] });
};

export const deleteContentStatusInCache = ({
  queryClient,
  contentId,
  contentType,
  username,
  // delete_status deletes every journal entry for contentId server-side
  // alongside the status, so wiping the cache wholesale is correct there —
  // that's the default. delete_entry's "last finished entry" special case
  // only ever removes the ONE entry plus the status row; any other entry for
  // this same content id (e.g. an earlier queued/started one) still exists
  // server-side, so its caller opts out and relies on having already filtered
  // just the deleted entry out of the journal cache itself.
  removeJournalCache = true,
}) => {
  // directly remove the deleted status from the contentStatus cache
  queryClient.removeQueries({
    queryKey: ["contentStatus", contentId],
    exact: "true",
  });

  if (removeJournalCache) {
    queryClient.removeQueries({
      queryKey: ["journal", contentId],
      exact: "true",
    });
  }

  // directly remove the deleted status from all infStatuses caches
  queryClient.setQueriesData(
    libraryLists("infStatuses", username, contentType),
    (oldData) => {
      if (!oldData) return oldData;

      return {
        ...oldData,
        pages: oldData.pages.map((page) => ({
          ...page,
          items: page.items.filter((item) => item.content_id !== contentId),
        })),
      };
    },
  );

  // directly remove any entries corresponding to the content whose status was deleted from all infJournal caches
  queryClient.setQueriesData(
    libraryLists("infJournal", username, contentType),
    (oldData) => {
      if (!oldData) return oldData;

      return {
        ...oldData,
        pages: oldData.pages.map((page) => ({
          ...page,
          items: page.items.filter((item) => item.content_id !== contentId),
        })),
      };
    },
  );

  // for tvshows, need to invalidate the TVJournalSummary if any season statuses are deleted
  invalidateTVJournalSummary({
    queryClient,
    contentType,
  });

  // Explore-page collection cards display a viewer-specific "finished %" computed server-side.
  // Mark the queries stale (refetchType "none" avoids refetching while the explore page is in view);
  // the next mount of CollectionSection picks up the invalidation via refetchOnMount.
  queryClient.invalidateQueries({
    queryKey: ["exploreCollections"],
    refetchType: "none",
  });

  // See updateContentStatusInCache
  queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  // Track's summary strip is counted from the same rows
  queryClient.invalidateQueries({ queryKey: ["librarySummary"] });
};

/**
 * Applies a status-write response (create_status/edit_status) to the cache.
 * Covers both ways a TV write doesn't land where it was asked to:
 *
 * - it can *resolve* onto different content than requested (single-season
 *   series → season 1; a quick queue/start on a multi-season series → a
 *   specific season) — `data.contentId` is where it actually landed, which
 *   can differ from `requestedContentId`.
 * - it can *cascade* onto other content nobody wrote to directly (a season
 *   write recomputing its series, a series "finished" propagating to every
 *   season) — `data.affectedContentIds` names all of it, so nothing here has
 *   to re-derive the propagation rules.
 */
export const applyStatusWriteToCache = ({
  queryClient,
  data,
  requestedContentId,
  oldStatus,
  contentType,
  username,
}) => {
  const resolvedElsewhere = data.contentId !== requestedContentId;
  updateContentStatusInCache({
    queryClient,
    contentId: data.contentId,
    contentType,
    // oldStatus only describes the id the caller targeted; when the backend
    // resolved elsewhere, omitting it lets updateContentStatusInCache fall
    // back to its wider removal from stale infStatuses lists.
    oldStatus: resolvedElsewhere ? undefined : oldStatus,
    newStatus: data.newStatus,
    entryObject: data.entryObject,
    username,
  });
  applyAffectedStatusesToCache({
    queryClient,
    contentIds: data.affectedContentIds,
    contentType,
    username,
  });
  // Achievements refresh with the rest of ["dashboard"] in updateContentStatusInCache;
  // any unlock is toasted centrally (see utils/awardEvents.js)
};

/**
 * Applies a status-clear response (delete_status) to the cache. A series-wide
 * clear reports every season it wiped in `clearedContentIds` alongside its
 * own id, so purging that whole list (rather than just the requested id)
 * covers both a single clear and a series-wide one with the same call.
 */
export const applyStatusClearToCache = ({
  queryClient,
  data,
  requestedContentId,
  contentType,
  username,
}) => {
  (data.clearedContentIds ?? [requestedContentId]).forEach((contentId) =>
    deleteContentStatusInCache({
      queryClient,
      contentId,
      contentType,
      username,
    }),
  );
  applyAffectedStatusesToCache({
    queryClient,
    contentIds: data.affectedContentIds,
    contentType,
    username,
  });
};

export const updateEntryInCache = ({
  queryClient,
  entryObject,
  contentType,
  username,
}) => {
  // directly overwrite the journal cache with the new entry
  queryClient.setQueryData(["journal", entryObject.content_id], (oldData) => {
    if (!oldData) return oldData;

    return oldData.map((entry) =>
      entry.journal_id === entryObject.journal_id ? entryObject : entry,
    );
  });

  // invalidate infStatuses to ensure any changes to finished_date or log_date are reflected in the sort order
  entryObject.status !== "finished" &&
    queryClient.invalidateQueries(
      libraryLists("infStatuses", username, contentType, entryObject.status),
    );

  // invalidate infJournal to ensure any changes to finished_date or log_date are reflected in the sort order
  entryObject.status === "finished" &&
    queryClient.invalidateQueries(
      libraryLists("infJournal", username, contentType),
    );

  // for tvshows, need to invalidate the TVJournalSummary if any season's entries mutate
  invalidateTVJournalSummary({
    queryClient,
    contentType,
  });

  // Finish dates order the dashboard's "Recently finished" row
  queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  // Track's summary strip is counted from the same rows
  queryClient.invalidateQueries({ queryKey: ["librarySummary"] });
};

export const deleteEntryFromCache = ({
  queryClient,
  contentId,
  contentType,
  deletedEntryId,
  clearedContentIds, // backend dropped this content's status row along with the entry
  affectedContentIds, // other content the backend's recompute (or sweep) moved — e.g. the series, or seasons a "Finish series" cascade invented and this deletion orphaned
  username,
}) => {
  // directly remove the deleted entry from the journal cache
  queryClient.setQueryData(["journal", contentId], (oldData) => {
    if (!oldData) return oldData;
    return oldData.filter((entry) => entry.journal_id !== deletedEntryId);
  });

  // Deleting the only finished entry also deletes the status server-side, so
  // the cached status has to go rather than linger as a stale "finished". Only
  // the one entry was deleted server-side (not every entry for this content),
  // so the journal cache keeps whatever the precise filter above left it with
  // rather than being wiped wholesale.
  (clearedContentIds ?? []).forEach((clearedContentId) =>
    deleteContentStatusInCache({
      queryClient,
      contentId: clearedContentId,
      contentType,
      username,
      removeJournalCache: false,
    }),
  );

  applyAffectedStatusesToCache({
    queryClient,
    contentIds: affectedContentIds,
    contentType,
    username,
  });

  // directly remove the deleted entry from the infJournal caches
  queryClient.setQueriesData(
    libraryLists("infJournal", username, contentType),
    (oldData) => {
      if (!oldData) return oldData;

      return {
        ...oldData,
        pages: oldData.pages.map((page) => ({
          ...page,
          items: page.items.filter(
            (item) => item.journal_id !== deletedEntryId,
          ),
        })),
      };
    },
  );

  // for tvshows, need to invalidate the TVJournalSummary if a season's entry is deleted
  invalidateTVJournalSummary({
    queryClient,
    contentType,
  });

  // See updateEntryInCache
  queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  // Track's summary strip is counted from the same rows
  queryClient.invalidateQueries({ queryKey: ["librarySummary"] });
};

export const updateFollowStatusInCache = ({
  queryClient,
  currentUser,
  username,
  followStatus,
}) => {
  if (!currentUser) return;

  const currentUserData = {
    familyName: currentUser.familyName,
    givenName: currentUser.givenName,
    id: currentUser.id,
    avatar_url: currentUser.avatar_url,
    username: currentUser.username,
  };

  queryClient.setQueryData(["profile", username], (oldData) => {
    if (!oldData) return oldData; // Do nothing if target profile not present in the cache

    return {
      ...oldData,
      isFollowing: followStatus,
      followers: followStatus
        ? [currentUserData, ...(oldData.followers || [])]
        : oldData.followers.filter(
            (user) => user.username !== currentUser.username,
          ),
    };
  });

  // Your own profile's following list and count. Refetched rather than edited
  // in place, since only the target's username is known here — its avatar and
  // names would be missing from the list.
  queryClient.invalidateQueries({
    queryKey: ["profile", currentUser.username],
  });

  queryClient.invalidateQueries({ queryKey: ["infFeed"] });
  // The "Following" tab in LibraryModal reads who the current user follows —
  // stale after any follow/unfollow.
  queryClient.invalidateQueries({ queryKey: ["following"] });
  // Confirms the change for every follow button (profile header, user search)
  toast.success(
    followStatus ? `Following @${username}` : `Unfollowed @${username}`,
  );

  // Getting started's "Follow 3 people" step
  queryClient.invalidateQueries({ queryKey: ACHIEVEMENTS_KEY });
};

/**
 * Single entry point for busting every cache a list mutation can affect.
 * Covers both cache families lists are read through:
 *   - ["list", listId] — the single-list detail view (open collection dialog,
 *     Cover/CoverCollection's passive cache read, CuratePreview)
 *   - the collections-of-lists views (infCollections / profile pick lists)
 *
 * listId is omitted only by createList, since nothing is cached under a new
 * id yet. `removed: true` (deleteList) drops the detail cache outright rather
 * than refetching a list that no longer exists.
 *
 * Deliberately does NOT bust ["exploreCollections"] — list-metadata edits are
 * a rare admin action there and staleness is tolerable; the finished-% those
 * cards show is kept live by the status mutations in this file instead (see
 * applyStatusWriteToCache / applyStatusClearToCache).
 */
export const invalidateListCaches = ({
  queryClient,
  listId,
  origin = "user",
  profileKind = null,
  username,
  contentType,
  removed = false,
}) => {
  if (listId) {
    const listQuery = { queryKey: ["list", listId] };
    removed
      ? queryClient.removeQueries(listQuery)
      : queryClient.invalidateQueries(listQuery);
  }

  // Getting started's top 5 and collection steps, and a badge if a profile
  // list add finished the item
  queryClient.invalidateQueries({ queryKey: ACHIEVEMENTS_KEY });

  if (origin === "profile") {
    // Profile pick lists (top-1/top-3/top-5): bust the specific profile collection query.
    queryClient.invalidateQueries({
      queryKey: [
        "collection",
        "profile",
        { profileKind, contentType, username },
      ],
    });
    // Also bust the finished-journal so onboarding additions appear on the track page.
    queryClient.invalidateQueries(
      libraryLists("infJournal", username, contentType, "finished"),
    );
  } else {
    // User (and canonical-admin) collections: bust the dropdown + the paginated list views.
    // Bust the "which lists contain this item?" dropdown data for every content item.
    queryClient.invalidateQueries({
      queryKey: ["collections", "forContent"],
    });
    // Bust the collections page filtered to the mutated list's content type.
    // contentType here is always the list's type (e.g. "movie", "mixed"), not the
    // item's type — mutations return list_content_type in their response.
    queryClient.invalidateQueries({
      queryKey: ["infCollections", contentType],
    });
    // Always bust the unfiltered ("All") view regardless of the list's type.
    queryClient.invalidateQueries({
      queryKey: ["infCollections", "all"],
    });
  }
};

// Invalidates only the saved-collections list queries. Called on save/unsave so
// that unsaving from within /curate/u/:username/saved removes the stale entry in the background.
export const invalidateSavedCollections = ({ queryClient }) => {
  queryClient.invalidateQueries({
    predicate: (query) =>
      query.queryKey[0] === "infCollections" &&
      query.queryKey[2]?.view === "saved",
  });
};

export const invalidateInfQueries = ({ queryClient }) => {
  queryClient.invalidateQueries({ queryKey: ["infFeed"] });
  queryClient.invalidateQueries({ queryKey: ["infStatuses"] });
  queryClient.invalidateQueries({ queryKey: ["infJournal"] });
  queryClient.invalidateQueries({ queryKey: ["librarySummary"] });
  queryClient.invalidateQueries({ queryKey: ["infCollections"] });
};

// REPLACED WITH UPDATE_LIST_CACHE - IDEALLY SHOULD HAVE SEPARATE ONES SO WE DON'T INVALIDATE EVERYTHING WHEN WE KNOW EXACTLY WHAT'S BEING DELETED?
// export const deleteItemFromListCache = ({ queryClient, listId, contentId }) => {
//   queryClient.setQueriesData(
//     { queryKey: ["infCollections"], exact: false },
//     (oldData) => {
//       if (!oldData) return oldData;

//       return {
//         ...oldData,
//         pages: oldData.pages.map((page) => ({
//           ...page,
//           items: page.items.map((collection) => {
//             if (collection.list_id !== listId) return collection;

//             return {
//               ...collection,
//               items: collection.items.filter(
//                 (item) => item.content_id !== contentId
//               ),
//             };
//           }),
//         })),
//       };
//     }
//   );
// };

export const updateProfileInCache = ({
  queryClient,
  newUserDetails,
  onUserUpdate,
  usernameChanged = false,
}) => {
  // Update auth context user state and persist to localStorage
  onUserUpdate(newUserDetails);

  // Only invalidate profile when username hasn't changed, because if username changes
  // then the profile query gets refetched anyway due to query key changing
  if (!usernameChanged) {
    queryClient.invalidateQueries(["profile", newUserDetails.username]);
  }
  // Getting started's top 5 step follows the primary content type
  queryClient.invalidateQueries({ queryKey: ACHIEVEMENTS_KEY });
};
